import React, { useMemo, useRef } from 'react';
import {
	Stack,
	AppBar,
	Toolbar,
	Typography,
	Dialog,
	IconButton,
	TableContainer,
	Table,
	TableCell,
	TableRow,
	TableHead,
	TableBody,
	Divider,
	Box
} from '@mui/material';
import { isEmpty } from 'lodash';
import moment from 'moment';
import ReactToPrint from 'react-to-print';
import PrintRoundedIcon from '@mui/icons-material/PrintRounded';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCloudArrowDown } from '@fortawesome/free-solid-svg-icons';
import * as XLSX from 'xlsx/xlsx.mjs';
import NoData from '../NoData';

/**
 * Esqueleto comun de los reportes de cartera agrupados: encabezado, exportacion a
 * Excel, impresion, subtotales por grupo y total general.
 *
 * Cada reporte solo aporta sus datos y la definicion de columnas; la tabla, el
 * Excel y los totales salen de esa misma definicion, de modo que agregar una
 * columna sea un solo cambio y no se desincronice lo que se ve con lo que se
 * exporta.
 *
 * Forma de `columnas`:
 *   { key, label, align, total?, formatoTotal?, valor: (fila) => nodo,
 *     excel?: (fila) => valor }
 *   - `total: true` suma la columna por grupo y en el total general.
 *   - `formatoTotal` da formato a esa suma (por defecto se muestra cruda).
 *   - `excel` devuelve el valor crudo (numero/fecha) para que Excel no lo trate
 *     como texto. Si se omite se usa `valor`.
 *
 * `agruparPor` recibe una fila y devuelve el nombre del grupo.
 */
const ReporteAgrupado = ({
	tituloReporte,
	nombreArchivo,
	parametros,
	columnas,
	data,
	agruparPor,
	etiquetaGrupo = 'Gestor',
	contexto
}) => {
	const compRef = useRef();
	const { title, slogan, icon, user, rol } = contexto;

	const columnasTotalizadas = useMemo(() => columnas.filter((col) => col.total), [columnas]);

	// Las columnas anteriores a la primera totalizada se fusionan en la celda con
	// la etiqueta del total; de ahi en adelante cada columna lleva su suma.
	const primeraTotalizada = useMemo(
		() => columnas.findIndex((col) => col.total),
		[columnas]
	);
	const columnasPie = useMemo(
		() => columnas.slice(primeraTotalizada),
		[columnas, primeraTotalizada]
	);

	const sumar = useMemo(
		() => (filas) =>
			columnasTotalizadas.reduce((acumulado, col) => {
				acumulado[col.key] = filas.reduce((suma, f) => suma + (f[col.key] ?? 0), 0);
				return acumulado;
			}, {}),
		[columnasTotalizadas]
	);

	// Los totales se derivan de los datos y no se acumulan durante el render, para
	// que volver a renderizar no los duplique.
	const grupos = useMemo(() => {
		const porGrupo = new Map();
		data.forEach((fila) => {
			const nombre = agruparPor(fila);
			if (!porGrupo.has(nombre)) {
				porGrupo.set(nombre, []);
			}
			porGrupo.get(nombre).push(fila);
		});

		return Array.from(porGrupo, ([nombre, filas]) => ({
			nombre,
			filas,
			totales: sumar(filas)
		})).sort((a, b) => a.nombre.localeCompare(b.nombre));
	}, [data, agruparPor, sumar]);

	const totalGeneral = useMemo(() => sumar(data), [data, sumar]);

	const exportExcel = () => {
		const filas = data.map((fila) => {
			const salida = { [etiquetaGrupo]: agruparPor(fila) };
			columnas.forEach((col) => {
				salida[col.label] = col.excel ? col.excel(fila) : col.valor(fila);
			});
			return salida;
		});

		const libro = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(libro, XLSX.utils.json_to_sheet(filas), 'Reporte');
		XLSX.writeFile(libro, `${nombreArchivo}.xlsx`);
	};

	const FilaTotales = ({ etiqueta, totales, resaltado }) => (
		<TableRow>
			<TableCell
				colSpan={primeraTotalizada}
				align="right"
				sx={{ fontWeight: 'bold', fontSize: 10 }}
			>
				{etiqueta}
			</TableCell>
			{columnasPie.map((col) => (
				<TableCell
					key={col.key}
					align={col.align}
					sx={{
						fontWeight: 'bold',
						fontSize: 10,
						color: resaltado ? 'primary.main' : 'text.primary'
					}}
				>
					{col.total
						? col.formatoTotal
							? col.formatoTotal(totales[col.key])
							: totales[col.key]
						: ''}
				</TableCell>
			))}
		</TableRow>
	);

	return (
		<div>
			<Dialog fullScreen open={true}>
				<AppBar sx={{ position: 'relative' }}>
					<Toolbar>
						<img loading="lazy" src={icon} alt="logo" style={{ height: 40 }} />
						<Typography
							sx={{ ml: 2, flex: 1, textAlign: 'center' }}
							variant="h5"
							component="div"
						>
							{`${title} `}
						</Typography>
						<img loading="lazy" src={icon} alt="logo" style={{ height: 40 }} />
					</Toolbar>
				</AppBar>

				<Stack display="flex" justifyContent="center">
					<Stack
						direction={'row'}
						justifyContent="space-between"
						paddingLeft={5}
						paddingRight={5}
						marginTop={1}
						marginBottom={1}
					>
						<IconButton onClick={() => exportExcel()} sx={{ color: 'primary.main' }}>
							<FontAwesomeIcon
								icon={faCloudArrowDown}
								style={{ fontSize: 22, width: 22 }}
							/>
						</IconButton>

						<Stack>
							<Typography
								sx={{
									color: 'text.secondary',
									textAlign: 'center',
									fontWeight: 'bold'
								}}
							>
								{slogan}
							</Typography>
							<Typography
								sx={{
									color: 'text.secondary',
									textAlign: 'center',
									fontWeight: 'bold'
								}}
								variant="h6"
								component="div"
							>
								{tituloReporte}
							</Typography>
						</Stack>

						<ReactToPrint
							trigger={() => (
								<IconButton sx={{ color: 'info.main' }}>
									<PrintRoundedIcon style={{ fontSize: 22, width: 22 }} />
								</IconButton>
							)}
							content={() => compRef.current}
							pageStyle={`
								@media print {
									html, body {
										height: initial !important;
										overflow: initial !important;
										-webkit-print-color-adjust: exact;
									}
								}
								@page { size: landscape; margin: 5mm; }
							`}
						/>
					</Stack>

					<Box sx={{ paddingX: '22px', textAlign: 'center' }}>
						<Stack direction={'row'} justifyContent="space-between">
							<Stack>
								<Typography
									sx={{ color: 'text.secondary', textAlign: 'left' }}
									fontSize={11}
								>
									Parametros:
								</Typography>
								{parametros.map((p) => (
									<Typography key={p.label} fontSize={11} textAlign="left">
										{`${p.label}: ${p.valor}`}
									</Typography>
								))}
							</Stack>
							<Stack>
								<Typography
									sx={{ color: 'text.secondary', textAlign: 'left' }}
									fontSize={11}
								>
									Usuario:
								</Typography>
								<Typography fontSize={11} textAlign="left">
									{user} - {rol}
								</Typography>
								<Typography fontSize={11} textAlign="left">
									{`Fecha y Hora: ${moment().format('DD/MM/YYYY hh:mm A')}`}
								</Typography>
							</Stack>
						</Stack>
					</Box>
				</Stack>

				<Box ref={compRef} sx={{ paddingX: '22px', textAlign: 'center' }}>
					<Divider sx={{ marginY: '14px' }} />
					{isEmpty(data) ? (
						<NoData />
					) : (
						<>
							{grupos.map((grupo) => (
								<Stack key={grupo.nombre} sx={{ marginBottom: '18px' }}>
									<Stack direction="row" spacing={1}>
										<Typography
											sx={{ color: 'text.secondary', textAlign: 'left' }}
											fontSize={11}
										>
											{`${etiquetaGrupo}:`}
										</Typography>
										<Typography
											sx={{ textAlign: 'left', fontWeight: 'bold' }}
											fontSize={11}
										>
											{`${grupo.nombre} (${grupo.filas.length} ${
												grupo.filas.length === 1 ? 'credito' : 'creditos'
											})`}
										</Typography>
									</Stack>

									<Divider />

									<TableContainer>
										<Table size="small">
											<TableHead>
												<TableRow>
													{columnas.map((col) => (
														<TableCell
															key={col.key}
															align={col.align}
															className="text-primary fw-bold"
															style={{ fontSize: 10 }}
														>
															{col.label}
														</TableCell>
													))}
												</TableRow>
											</TableHead>
											<TableBody>
												{grupo.filas.map((fila) => (
													<TableRow key={fila.id}>
														{columnas.map((col) => (
															<TableCell
																key={col.key}
																align={col.align}
																style={{ fontSize: 9 }}
															>
																{col.valor(fila)}
															</TableCell>
														))}
													</TableRow>
												))}
												<FilaTotales
													etiqueta={`Total ${grupo.nombre}:`}
													totales={grupo.totales}
												/>
											</TableBody>
										</Table>
									</TableContainer>
								</Stack>
							))}

							<Divider sx={{ marginY: '10px' }} />

							<TableContainer>
								<Table size="small">
									<TableBody>
										<FilaTotales
											etiqueta={`TOTAL GENERAL (${data.length} creditos):`}
											totales={totalGeneral}
											resaltado
										/>
									</TableBody>
								</Table>
							</TableContainer>
						</>
					)}
				</Box>
			</Dialog>
		</div>
	);
};

export default ReporteAgrupado;
